<template>
    <div class="poi-picker">
        <div class="poi-picker__header" v-if="mode !== 'general'">
            <div class="poi-picker__header__title h2">
                <template v-if="mode === 'route'">Entlang der Route anzeigen</template>
                <template v-else>In der Umgebung anzeigen</template>
            </div>
            <POICorridorRadiusSettings v-if="mode === 'route'" />
        </div>
        <div class="poi-picker__list" :class="{'is-show-all': showAll}" :style="{height: itemListHeight + 'px'}">
            <div class="poi-picker__list__inner" ref="item-list-inner">
                <div class="poi-picker__item" v-for="item in orderedItems" :key="item.value" @click="itemClickHandler(item.value, item.trackingLabel)" :class="{'is-item-selected': isItemSelected(item.value)}">
                    <div class="poi-picker__item__icon">
                        <Loader class="poi-picker__item__icon__loader" v-if="isItemLoading(item.value)"/>
                        <icon v-else><POIIcon :icon="item.icon"/></icon>
                    </div>
                    <div class="poi-picker__item__label" v-html="item.label"></div>
                </div>
                <!-- Show more -->
                <SmrTooltip content="Mehr Kategorien anzeigen" position="bottom-start">
                    <div class="poi-picker__item poi-picker__item--more" @click="toggleClickHandler">
                        <div class="poi-picker__item__icon">
                            <icon><IconMore/></icon>
                        </div>
                        <div class="poi-picker__item__label">Mehr anzeigen</div>
                    </div>
                </SmrTooltip>
                
                <!-- Show less -->
                <SmrTooltip content="Weniger Kategorien anzeigen" position="bottom-start">
                    <div class="poi-picker__item poi-picker__item--les" @click="toggleClickHandler">
                        <div class="poi-picker__item__icon">
                            <icon><IconMore/></icon>
                        </div>
                        <div class="poi-picker__item__label">Weniger anzeigen</div>
                    </div>
                </SmrTooltip>
            </div>
        </div>
        
    </div>
</template>

<script>
import eventBus from '@/utils/event-bus';
import { mapGetters } from 'vuex';
import poiElements from '@data/poiElements.js'
import POIIcon from '@/components/POIIcon.vue'
import Loader from '@/components/Loader.vue'
import Icon from '@/components/Icon.vue'
import IconMore from '@/components/icons/IconMore.vue'
import { PRICE_SORTING_MODES, getPoiBySlug } from '@/utils/poi-utils'
import { updatePOICategoryUrl } from '@/utils/url-utils'
import POICorridorRadiusSettings from '@/components/POICorridorRadiusSettings'
import SmrTooltip from '@/components/SmrTooltip.vue'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { updateQueryParams } from '@/utils/url-utils'

export default {
    components: {
        POIIcon,
        Loader,
        Icon,
        IconMore,
        POICorridorRadiusSettings,
        SmrTooltip,
    },
    props: {
        mode: {
            type: String,
            default: 'general'
        },
        visible: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            showAll: true,
            orderedItems: [],
            itemListHeight: 290,
            items: poiElements,
            priceSortingModes: PRICE_SORTING_MODES,
            searchedForBounds: false,
            categoryFromUrl: [],
        }
    },
    computed: {
        ...mapGetters(['mobile', 'selectedPOIs', 'loadingPOIs', 'mapMoved', 'mapFullyLoaded', 'categoriesFromUrl', 'activePoiId', 'poiSettings', 'sidebarDetailOpen']),
        singleSelectedPOI() {
            if(this.singlePOIselected) {
                return this.poiItems.find(item => {
                    return item.value === this.selectedPOIs[0]
                })
            } else {
                return false
            }
        },
    },
    created() {
        this.orderItems(true, true)
        this.addListeners();
    },
    beforeDestroy() {
        this.removeListeners()
    },
    watch: {
        visible(val) {
            if(!val && this.selectedPOIs.length) {
                this.$store.dispatch('deselectAllPOIs');
            }
        },
        mapFullyLoaded(val) {
            if(val && this.categoriesFromUrl.length > 0) {
                this.categoriesFromUrl.forEach(value => {
                    if(value) this.toggleItem(value)
                })
               this.$store.commit('setCategoriesFromUrl', [])
            }
        },
        selectedPOIs(val) {
            if(!this.activePoiId) this.updateCurrentUrl(val)
            // on page reload, show selected POIs in front if there are hidden POIs
            const hiddenPOIs = this.orderedItems.slice(9).map(items => items.value)
            if (!this.showAll && this.selectedPOIs.some(poi => hiddenPOIs.includes(poi))) {
                this.orderItems(true);
            }

            // reset opening POI after login
            this.$store.commit('setOpenPOIAfterLogin', false)

            //tracking poi
            this.trackingInfo = [];
            let trackingValue = [];
            val.forEach(el => {
                let trackingItem = this.orderedItems.filter(orderItem => orderItem.value === el)
                this.trackingInfo.push(trackingItem[0].trackingLabel);
                trackingValue.push(trackingItem[0].trackingValue);
            })
            let trackingLabel = this.trackingInfo.toString().replace(/,/g, '_');

            if(val.length) {
                trackEvent({ category: 'Route Planner', action: 'POI Categories', label: trackingLabel})
                trackingValue = trackingValue ? trackingValue.toString().replace(/,/g, '-') : '-';
                trackGa4Event({ga4Parameter1Name: 'poi_categories', ga4Parameter1Value: trackingValue, ga4Event: 'route_planner'})
            }
        },
        sidebarDetailOpen(newVal, oldVal) {
            if(oldVal && !newVal && this.selectedPOIs.length) {
                updateQueryParams(this.$route, { poiOptions: this.poiSettings })
            }
        }
    },
    methods: {
        addListeners() {
            eventBus.$on('scrollToPOIPicker', this.scrollToPOIPickerHandler)
            eventBus.$on('activatePOICategory', this.activatePOICategoryHandler)
            eventBus.$on('urlChanged', this.urlChangedHandler)
        },
        removeListeners() {
            eventBus.$off('scrollToPOIPicker', this.scrollToPOIPickerHandler)
            eventBus.$off('activatePOICategory', this.activatePOICategoryHandler)
            eventBus.$off('urlChanged', this.urlChangedHandler)
        },
        toggleClickHandler() {
            this.toggleShowAll();
        },
        itemClickHandler(poi) {
            // only use settings from url query once and remove when manually toggling items
            this.$store.commit('setQueryPoiSettings', false)
            this.toggleItem(poi);
        },
        scrollToPOIPickerHandler() {
            this.scrollToPOIPicker(false);
        },
        activatePOICategoryHandler(poiValues) {
            if(this.mapFullyLoaded) {
                poiValues.forEach(value => {
                    if(value) this.toggleItem(value)
                })
            }
        },
        urlChangedHandler(e) {
            if(!this.mapFullyLoaded) return

            if(e.to.name === 'Main') {
                this.deselectAllItems()
            } else if(e.to.name === 'POI Category') {
                const poiCategories = e.to.params.poicategory.split(',')
                if(poiCategories.length === 0 || poiCategories[0] === '') {
                   this.deselectAllItems() 
                } else {
                    const pois = poiCategories.map(category => getPoiBySlug(category).value)
                    // deactivate the unneeded pois
                    this.selectedPOIs.forEach(selectedPOI => {
                        if(!pois.includes(selectedPOI)) {
                            this.deselectItem(selectedPOI)
                        }
                    })
                    // activate the needed ones
                    pois.forEach(poi => {
                        if(!this.selectedPOIs.includes(poi)) {
                            this.selectItem(poi)
                        }
                    })
                }
            }
        },
        toggleShowAll() {
            trackEvent({ category: 'Route Planner', action: 'POI Categories', label: this.showAll ? 'Show Less' : 'Show More'})
            trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: this.showAll ? 'show_less_pois' : 'show_more_pois', ga4Event: 'route_planner'})

            this.showAll = !this.showAll;
            if(!this.showAll) {
                this.orderItems();
            }
            this.toggleHeight();
        },
        toggleItem(poi) {
            if(this.loadingPOIs.includes(poi)) {
                this.$store.dispatch('cancelPOILoading', poi);
                if(!this.selectedPOIs.includes(poi)) return false;
            }

            if(this.selectedPOIs.includes(poi)) {
                this.deselectItem(poi);
            } else {
                // deselect all other POIs when favourites are selected 
                if (poi === 'favourites') {
                    const selectedPOIsClone = [...this.selectedPOIs]
                    selectedPOIsClone.forEach(selectedPoi => {
                        this.deselectItem(selectedPoi)
                    })
                    this.selectItem('favourites');
                } else {
                    this.deselectItem('favourites');
                    this.selectItem(poi);
                }
            }
        },
        deselectItem(poi) {
            if(this.loadingPOIs.includes(poi)) this.$store.dispatch('cancelPOILoading', poi)
            this.$store.dispatch('deselectPOI', poi);
        },
        async selectItem(poi) {
            // show disclaimer for gasStations
            if(poi === 'gasStations' || poi === 'chargingStations') {
                eventBus.$emit('showDisclaimerInfo', { poiType: poi })
            }
            this.searchedForBounds = this.mapBounds
            await this.loadPOIs(poi);
            setTimeout(() => {
                this.scrollToPOIPicker();
            }, 100);
        },
        isItemSelected(poi) {
            return this.selectedPOIs.includes(poi);
        },
        isItemLoading(poi) {
            return this.loadingPOIs.includes(poi);
        },
        deselectAllItems() {
            if(this.selectedPOIs.length > 0) {
                this.selectedPOIs.forEach(selectedPoi => {
                    this.deselectItem(selectedPoi)
                })
            }
        },
        orderItems(forceOrdering = false, initial = false) {
            if(initial) {
                const hiddenPOIs = this.items.slice(9).map(items => items.value)
                if(!this.showAll && !this.selectedPOIs.some(poi => hiddenPOIs.includes(poi))) {
                    this.orderedItems = [...this.items]
                    return
                }
            }
            if(!forceOrdering && !this.extendedItemsAffected()) return false
            let selected = [];
            let notSelected = [];
            this.items.forEach(item => {
                if(this.isItemSelected(item.value)) {
                    selected.push(item);
                } else {
                    notSelected.push(item);
                }
            })
            this.orderedItems = [...selected, ...notSelected];
        },
        getItemListHeight() {
            if(!this.showAll) {
                return 140;
            } else {
                return this.$refs['item-list-inner'].clientHeight;
            }
        },
        getRowCount() {
            return Math.ceil((this.items.length + 1) / 5);
        },
        toggleHeight() {
            this.itemListHeight = this.getItemListHeight();
        },
        extendedItemsAffected() {
            let affected = false;
            this.selectedPOIs.forEach(poi => {
                let poiIndex = this.orderedItems.findIndex(item => item.value === poi);
                if(poiIndex > 8) {
                    affected = true;
                    return true;
                }
            })
            return affected;
        },
        async loadPOIs(poi) {
            await this.$store.dispatch('loadPOIResultsByType', { type: poi });
            // check wether mapbounds were moved and more than one poi is selected 
            if(this.mapMoved && this.selectedPOIs.length > 1) {
                const poisToLoad = this.selectedPOIs.filter(selectedPOI => selectedPOI !== poi)
                for(const poiToLoad of poisToLoad) {
                    await this.$store.dispatch('loadPOIResultsByType', { type: poiToLoad });
                }
            }
        },
        scrollToPOIPicker(onlyScrollDown = true) {
            eventBus.$emit('scrollToSidebar', {target: this.$el, onlyScrollDown})
        },
        updateCurrentUrl(selectedPois) {
            updatePOICategoryUrl(this.$route, [...selectedPois])
            updateQueryParams(this.$route, { poiOptions: this.poiSettings })
        }
    }
}
</script>

<style lang="scss">
.poi-picker {
    $block: &;
    padding-top: 16px;
    padding-bottom: 16px;
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        &__title {
            padding: 1px 0; // so it doesn't move around when the "Options" button get's showed
        }
        &__settings {
            display: flex;
            align-items: center;
            cursor: pointer;
            .no-touch &:hover {
                #{$block}__header__settings__icon {
                    color: $color-black;
                }
            }
            &__text {
                margin-right: 2px;
                font-weight: $font-weight-bold;
                font-size: 12px;
            }
            &__icon {
                position: relative;
                transition: color 0.15s;
                color: $color-grey-dark;
                margin-top: -1px;
                line-height: 1;
                height: 24px;
            }
        }
    }
    &__list {
        overflow: hidden;
        transition: height 0.4s $ease-out-quart;
        &__inner {
            display: grid;
            grid-column-gap: 4px;
            grid-row-gap: 8px;
            grid-template-columns: repeat(5, 1fr);
            position: relative;
        }
    }
    &__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        min-height: 66px;
        @media (hover: hover) {
            &:hover {
                #{$block}__item__icon {
                    background-color: $color-black-01;
                }
            }
        }
        &--more {
            position: absolute;
            right: 0;
            width: calc(20% - 3.2px);
            top: 74px;
            background: white;
            .is-show-all & {
                opacity: 0;
                pointer-events: none;
            }
        }
        &__icon {
            transition: background-color 0.15s;
            width: 40px;
            height: 40px;
            border-radius: 40px;
            background-color: $color-black-005;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 4px;
            .is-item-selected & {
                background-color: $color-yellow !important;
            }
            svg {
                display: block;
                color: $color-grey-dark;
                overflow: visible;
                .is-item-selected & {
                    color: $color-black;
                }
            }
        }
        &__label {
            font-size: 11px;
            color: $color-grey;
            line-height: 1;
            text-align: center;
        }
    }
}
</style>